import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AtSign, Loader2, Handshake, Sparkles, ShieldAlert, Trophy, Star } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useTradeRecipient } from '../hooks/useTradeRecipient';
import { toast } from '../hooks/use-toast';

export default function PublicProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { isAuthed, user: me, openAuth } = useAuth();
  const { setRecipient } = useTradeRecipient();

  const [state, setState] = useState({ loading: true, user: null, stats: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, user: null, stats: null, error: null });
    (async () => {
      try {
        const res = await api.getPublicProfile(username);
        if (cancelled) return;
        if (!res?.user) {
          setState({ loading: false, user: null, stats: null, error: 'not_found' });
        } else {
          setState({ loading: false, user: res.user, stats: res.stats || null, error: null });
        }
      } catch (e) {
        if (!cancelled) {
          const notFound = e?.status === 404;
          setState({ loading: false, user: null, stats: null, error: notFound ? 'not_found' : (e.message || 'error') });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [username]);

  const startTrade = () => {
    if (!isAuthed) {
      toast({ title: 'Sign in first', description: 'Create an account so we can send your trade to @' + username + '.' });
      openAuth('login');
      return;
    }
    setRecipient({
      id: state.user.id,
      username: state.user.username,
      name: state.user.name,
    });
    toast({
      title: `Trading with @${state.user.username}`,
      description: 'Pick any sprite below and hit its Trade button to send the request.',
    });
    navigate('/');
  };

  const isSelf = isAuthed && me?.username && me.username.toLowerCase() === (username || '').toLowerCase();
  const initial = ((state.user?.name || state.user?.username || username || '?')[0] || '?').toUpperCase();

  return (
    <div
      className="min-h-screen"
      style={{
        background:
          'radial-gradient(circle at 10% 0%, #ff2bd6 0%, transparent 40%),' +
          'radial-gradient(circle at 100% 20%, #00b8ff 0%, transparent 40%),' +
          'radial-gradient(circle at 50% 100%, #ffd900 0%, transparent 45%),' +
          'linear-gradient(180deg, #ffe9fb 0%, #fff7d6 60%, #e9f7ff 100%)',
      }}
    >
      <div className="max-w-2xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        <button
          data-testid="public-profile-back-btn"
          onClick={() => navigate('/')}
          className="mb-4 h-9 px-3 rounded-xl border-2 border-[#3a1064] bg-white text-[#3a1064] font-black text-sm shadow-[0_3px_0_#3a1064] flex items-center gap-1.5 hover:bg-yellow-50"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={3} /> Back to tracker
        </button>

        {state.loading && (
          <div className="rounded-2xl bg-white border-[3px] border-[#3a1064] shadow-[0_6px_0_#3a1064] p-10 text-center">
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-[#ff2bd6]" />
            <div className="mt-3 font-black text-[#3a1064]">Loading @{username}…</div>
          </div>
        )}

        {!state.loading && !state.user && (
          <div className="rounded-2xl bg-white border-[3px] border-[#3a1064] shadow-[0_6px_0_#3a1064] p-8 text-center">
            <ShieldAlert className="h-10 w-10 mx-auto text-[#ff2bd6]" />
            <div className="mt-3 font-black text-2xl text-[#3a1064]">User not found</div>
            <p className="mt-2 font-bold text-sm text-[#3a1064]/70">
              We couldn&apos;t find <span className="font-black">@{username}</span> on Sprite Vault.
              Double-check the link, or ask them to share it again.
            </p>
            <button
              onClick={() => navigate('/')}
              className="mt-5 h-11 px-5 rounded-xl bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black border-[3px] border-[#3a1064] shadow-[0_4px_0_#3a1064]"
            >
              Explore the vault
            </button>
          </div>
        )}

        {!state.loading && state.user && (
          <div className="rounded-2xl bg-white border-[3px] border-[#3a1064] shadow-[0_8px_0_#3a1064] overflow-hidden">
            <div
              className="p-5 sm:p-7 text-center"
              style={{ background: 'linear-gradient(135deg,#ffd900,#ff2bd6 50%,#9d3dff)' }}
            >
              <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-3xl bg-white border-[4px] border-[#3a1064] shadow-[0_6px_0_#3a1064] overflow-hidden mx-auto -rotate-3 flex items-center justify-center text-5xl sm:text-6xl font-black text-[#ff2bd6]">
                {state.user.picture ? (
                  <img src={state.user.picture} alt={state.user.username} className="h-full w-full object-cover" />
                ) : (
                  initial
                )}
              </div>
              <div className="mt-4 text-white font-black text-2xl sm:text-3xl drop-shadow-[0_2px_0_#3a1064]">
                {state.user.name || state.user.username}
              </div>
              <div className="mt-1 flex items-center justify-center gap-1.5 flex-wrap">
                <span className="inline-flex items-center gap-1 text-xs font-black uppercase px-2.5 py-1 rounded-full bg-[#3a1064] text-yellow-300 border-2 border-yellow-300">
                  <AtSign className="h-3.5 w-3.5" /> {state.user.username}
                </span>
                {state.user.role === 'admin' && (
                  <span className="inline-flex items-center gap-1 text-xs font-black uppercase px-2.5 py-1 rounded-full bg-yellow-300 text-[#3a1064] border-2 border-[#3a1064]">
                    Dev
                  </span>
                )}
              </div>
              <p className="mt-3 text-white/90 font-bold text-xs sm:text-sm max-w-md mx-auto">
                Sprite Vault trader · Ready to swap Fortnite Sprites with the community.
              </p>
            </div>

            <div className="p-5 sm:p-6 space-y-3 bg-white">
              {state.stats && (
                <div data-testid="public-profile-stats" className="grid grid-cols-3 gap-2 sm:gap-3">
                  <div className="rounded-xl bg-yellow-50 border-2 border-[#3a1064] p-3 text-center shadow-[0_3px_0_#3a1064]">
                    <Trophy className="h-5 w-5 mx-auto text-[#ff2bd6]" strokeWidth={3} />
                    <div className="mt-1 font-black text-xl text-[#3a1064] leading-none">{state.stats.owned}</div>
                    <div className="mt-1 text-[10px] font-black uppercase text-[#3a1064]/70 tracking-wide">Owned</div>
                  </div>
                  <div className="rounded-xl bg-pink-50 border-2 border-[#3a1064] p-3 text-center shadow-[0_3px_0_#3a1064]">
                    <Star className="h-5 w-5 mx-auto text-[#ffb800]" strokeWidth={3} fill="currentColor" />
                    <div className="mt-1 font-black text-xl text-[#3a1064] leading-none">{state.stats.mastered}</div>
                    <div className="mt-1 text-[10px] font-black uppercase text-[#3a1064]/70 tracking-wide">Mastered</div>
                  </div>
                  <div className="rounded-xl bg-cyan-50 border-2 border-[#3a1064] p-3 text-center shadow-[0_3px_0_#3a1064]">
                    <Handshake className="h-5 w-5 mx-auto text-[#00b8ff]" strokeWidth={3} />
                    <div className="mt-1 font-black text-xl text-[#3a1064] leading-none">{state.stats.trades_completed}</div>
                    <div className="mt-1 text-[10px] font-black uppercase text-[#3a1064]/70 tracking-wide">Trades</div>
                  </div>
                </div>
              )}
              {isSelf ? (
                <>
                  <div className="rounded-xl border-2 border-dashed border-[#3a1064]/30 bg-yellow-50 p-4 text-center">
                    <div className="font-black text-[#3a1064]">This is your public trade page.</div>
                    <div className="text-xs font-bold text-[#3a1064]/70 mt-1">
                      Share this link — anyone opening it lands here and can trade with you in one tap.
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/profile')}
                    className="w-full h-12 rounded-xl border-[3px] border-[#3a1064] bg-white hover:bg-yellow-50 text-[#3a1064] font-black shadow-[0_4px_0_#3a1064]"
                  >
                    Manage my profile
                  </button>
                </>
              ) : (
                <>
                  <button
                    data-testid="public-profile-trade-btn"
                    onClick={startTrade}
                    className="w-full h-14 rounded-xl border-[3px] border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black text-base sm:text-lg shadow-[0_5px_0_#3a1064] flex items-center justify-center gap-2 uppercase tracking-wide"
                  >
                    <Handshake className="h-5 w-5" strokeWidth={3} />
                    Trade with @{state.user.username}
                  </button>
                  <div className="text-[11px] font-bold text-[#3a1064]/70 text-center flex items-center justify-center gap-1.5">
                    <Sparkles className="h-3 w-3 text-[#ff2bd6]" />
                    You&apos;ll pick which sprite to offer next — {state.user.username} will get a notification.
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
